import { Box, Card, Flex, Heading, Text, TextField, Button, Separator } from "@radix-ui/themes";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { loginSchema, type LoginInput } from "@/lib/schemas";
import { getBoards, login, updatePreferredDisplayBoard } from "@/lib/api";
import {
  resolvePostLoginTarget,
  sanitizeInternalRedirect,
  toAppHref,
} from "@/lib/post-login-redirect";
import { useAuthStore } from "@/stores/auth.store";
import { FieldError } from "@/components/ui/field-error";
import { LanguageSwitcher } from "@/components/ui/language-switcher";

export function LoginPage() {
  const navigate = useNavigate();
  const redirect = new URLSearchParams(window.location.search).get("redirect");
  const { setAuth } = useAuthStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginInput) {
    const result = await login(data.email, data.password);
    if (!result.ok) {
      toast.error(result.error ?? t("auth.invalid_credentials"));
      return;
    }

    // pb.authStore est mis à jour automatiquement par authWithPassword()
    // → useAuthStore.onChange() synchronise Zustand — setAuth reste pour optimistic update
    setAuth(result.data.user, result.data.token);

    const explicitRedirect = sanitizeInternalRedirect(redirect ?? null);
    let boards: Array<{ id: string; slug: string }> = [];

    if (!explicitRedirect && result.data.user.preferred_display_board) {
      const boardsResult = await getBoards();
      if (boardsResult.ok) {
        boards = boardsResult.data.boards.map((board) => ({ id: board.id, slug: board.slug }));
      }
    }

    const target = resolvePostLoginTarget({
      explicitRedirect,
      preferredBoardId: result.data.user.preferred_display_board ?? null,
      boards,
    });

    if (target.kind === "redirect") {
      window.location.assign(toAppHref(target.path));
      return;
    }

    if (target.kind === "display") {
      navigate({ to: "/display/$slug", params: { slug: target.slug }, search: {} });
      return;
    }

    if (target.clearInvalidPreferred) {
      void updatePreferredDisplayBoard(null);
    }

    navigate({ to: "/dashboard" });
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{ minHeight: "100vh", backgroundColor: "var(--gray-2)" }}
    >
      <Box style={{ width: "100%", maxWidth: "400px" }} p="4">
        {/* Header */}
        <Flex direction="column" align="center" gap="1" mb="6">
          <Heading size="6" weight="bold">
            {t("common.app_name")}
          </Heading>
          <Text size="2" color="gray">
            {t("auth.login_subtitle")}
          </Text>
        </Flex>

        {/* Card */}
        <Card size="3">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Flex direction="column" gap="4">
              {/* Email */}
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="email">
                  {t("auth.email_label")}
                </Text>
                <TextField.Root
                  id="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                  size="3"
                  {...register("email")}
                  color={errors.email ? "red" : undefined}
                />
                <FieldError message={errors.email?.message} />
              </Flex>

              {/* Mot de passe */}
              <Flex direction="column" gap="1">
                <Flex justify="between" align="center">
                  <Text as="label" size="2" weight="medium" htmlFor="password">
                    {t("auth.password_label")}
                  </Text>
                  <Link to="/forgot-password" style={{ textDecoration: "none" }}>
                    <Text size="1" color="violet">
                      {t("auth.forgot_password")}
                    </Text>
                  </Link>
                </Flex>
                <TextField.Root
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  size="3"
                  {...register("password")}
                  color={errors.password ? "red" : undefined}
                />
                <FieldError message={errors.password?.message} />
              </Flex>

              {/* Submit */}
              <Button
                type="submit"
                size="3"
                style={{ width: "100%", marginTop: "var(--space-1)" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.login_pending") : t("auth.login_button")}
              </Button>
            </Flex>
          </form>

          <Separator size="4" my="4" />

          <Text size="2" color="gray" align="center" as="p">
            {t("auth.no_account")}{" "}
            <Link to="/register" style={{ textDecoration: "none" }}>
              <Text size="2" color="violet">
                {t("auth.create_account")}
              </Text>
            </Link>
          </Text>
        </Card>

        {/* Sélecteur de langue discret */}
        <Flex justify="center" mt="4">
          <LanguageSwitcher />
        </Flex>
      </Box>
    </Flex>
  );
}
